import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { getFullPathFromBreadcrumbs } from 'hooks/state/BreadcrumbSlice';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  applyNaturalExceptions,
  generateBreadcrumbLabel,
  getLastPathSegment,
} from 'components/breadcrumb/breadcrumbHelper';
import { generateBreadcrumbsFromPath } from 'components/breadcrumb/breadcrumbController';
import { getNavItems } from 'components/Sidebar/NavItems';
import {
  ATTRIBUTE_DETAIL_URLS,
  COMPOUND_BREADCRUMB_RULES,
  CUSTOM_BREADCRUMBS,
  NATURAL_BREADCRUMB_EXCEPTIONS,
} from 'components/breadcrumb/breadcrumbRules';
import { BreadcrumbItem } from 'components/breadcrumb/breadcrumbModel';
import { Box, Breadcrumbs, Menu, MenuItem, Typography } from '@mui/material';
import { tbPalette } from 'styles/colors';
import { TbIcon } from 'components/Figma';
import { Button } from 'components/Button';

function isSegmentInExceptions(segment: string, exceptions: typeof NATURAL_BREADCRUMB_EXCEPTIONS): boolean {
  return exceptions.some(rule => rule.target === segment || rule.successor === segment);
}

function shouldAppendBreadcrumb(existingBreadcrumbs: BreadcrumbItem[], newBreadcrumbs: BreadcrumbItem[]): boolean {
  if (existingBreadcrumbs.length === 0 || newBreadcrumbs.length === 0) {
    return false;
  }

  const fullExistingPath = existingBreadcrumbs.map(b => b.path).join('/');
  const fullNewPath = newBreadcrumbs.map(b => b.path).join('/');

  const lastExisting = existingBreadcrumbs[existingBreadcrumbs.length - 1];
  const lastNew = newBreadcrumbs[newBreadcrumbs.length - 1];

  if (lastExisting.path === lastNew.path || fullExistingPath === fullNewPath) {
    return false;
  }

  const existingSegment = getLastPathSegment(lastExisting.path, COMPOUND_BREADCRUMB_RULES);
  const newSegment = getLastPathSegment(lastNew.path, COMPOUND_BREADCRUMB_RULES);

  return (
    isSegmentInExceptions(existingSegment, NATURAL_BREADCRUMB_EXCEPTIONS) &&
    isSegmentInExceptions(newSegment, NATURAL_BREADCRUMB_EXCEPTIONS)
  );
}

const textLinkClassName = 'text-xs hover:underline hover:cursor-pointer';

export function Breadcrumb() {
  const { breadcrumbs, setBreadcrumbs, navigateToBreadcrumb, getBreadcrumbsWithFullPaths } = useBreadcrumbStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { messages } = useIntl();
  const initialLoadRef = useRef(true);
  const lastPathnameRef = useRef(location.pathname);
  const previousBreadcrumbsRef = useRef<BreadcrumbItem[]>(breadcrumbs);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previousBreadcrumbs, setPreviousBreadcrumbs] = useState<ReturnType<typeof getBreadcrumbsWithFullPaths>>([]);
  const [previousIcon, setPreviousIcon] = useState<React.ReactNode>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const ellipsisRef = useRef<HTMLButtonElement>(null);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (breadcrumbs.length > 0) {
      previousBreadcrumbsRef.current = breadcrumbs;
    }
  }, [location.pathname]);

  const getPageIcon = (breadcrumbsArray: ReturnType<typeof getBreadcrumbsWithFullPaths>): React.ReactNode => {
    if (breadcrumbsArray.length === 0) return null;
    const firstBreadcrumbPath = breadcrumbsArray[0].fullPath;
    const firstSegment = firstBreadcrumbPath.split('?')[0].split('/')[1] || '';

    const navItems = getNavItems();

    // Find matching nav item by checking if the route matches the segment
    const matchingNavItem = navItems.find(item => {
      const routeSegment = item.route.split('/')[1] || '';
      return routeSegment === firstSegment;
    });

    return matchingNavItem?.icon ?? null;
  };

  const handleEllipsisClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setDropdownOpen(true);
  };

  const handleDropdownClose = () => {
    setDropdownOpen(false);
    setAnchorEl(null);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownOpen && !anchorEl?.contains(event.target as Node)) {
        handleDropdownClose();
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [dropdownOpen, anchorEl]);

  const displayBreadcrumbs = getBreadcrumbsWithFullPaths();
  const currentIcon = getPageIcon(displayBreadcrumbs);

  const breadcrumbsToShow =
    isTransitioning && displayBreadcrumbs.length === 0 ? previousBreadcrumbs : displayBreadcrumbs;
  const iconToShow = isTransitioning ? previousIcon : currentIcon;

  const renderBreadcrumbLabel = (breadcrumbItem: BreadcrumbItem & { fullPath: string }) => {
    const slug = breadcrumbItem.fullPath;
    const customTranslator = Object.entries(CUSTOM_BREADCRUMBS)
      .sort((a, b) => b[0].length - a[0].length)
      .find(entry => slug.startsWith(entry[0]));

    if (customTranslator) {
      return customTranslator[1](breadcrumbItem);
    }

    const { messageId, label, hasMessage } = generateBreadcrumbLabel(slug, messages);

    if (hasMessage) {
      return <FormattedMessage id={messageId} defaultMessage={slug} />;
    }
    if (breadcrumbItem?.label) {
      return breadcrumbItem.label;
    }
    return label;
  };

  useEffect(() => {
    const hasNavigatedToNewPath = lastPathnameRef.current !== location.pathname;

    // Ignore Detail and Drilldown pages and let them deal with their breadcrumb directly
    if (!ATTRIBUTE_DETAIL_URLS.some(adu => location.pathname.startsWith(adu))) {
      if (initialLoadRef.current) {
        // Generate breadcrumbs if we don't have any persisted ones from URL parameters
        if (breadcrumbs.length === 0) {
          setIsTransitioning(true);
          setBreadcrumbs(generateBreadcrumbsFromPath());
        }
        initialLoadRef.current = false;
      } else if (hasNavigatedToNewPath) {
        setPreviousBreadcrumbs(displayBreadcrumbs);
        setPreviousIcon(getPageIcon(displayBreadcrumbs));
        setIsTransitioning(true);

        const newBreadcrumbs = generateBreadcrumbsFromPath();

        const oldBreadcrumbs = previousBreadcrumbsRef.current.length > 0 ? previousBreadcrumbsRef.current : breadcrumbs;

        const shouldAppend = shouldAppendBreadcrumb(oldBreadcrumbs, newBreadcrumbs);

        if (shouldAppend) {
          const newFullPath = getFullPathFromBreadcrumbs(newBreadcrumbs, newBreadcrumbs.length - 1);

          const siblingBreadcrumb: BreadcrumbItem = {
            ...newBreadcrumbs[newBreadcrumbs.length - 1],
            path: newFullPath,
          };
          const combined = [...oldBreadcrumbs, siblingBreadcrumb];

          const reordered = applyNaturalExceptions(combined, NATURAL_BREADCRUMB_EXCEPTIONS, COMPOUND_BREADCRUMB_RULES);
          setBreadcrumbs(reordered);
        } else {
          setBreadcrumbs(newBreadcrumbs);
        }
      }
    }

    lastPathnameRef.current = location.pathname;
  }, [location.pathname, setBreadcrumbs, breadcrumbs]);

  useEffect(() => {
    if (isTransitioning && displayBreadcrumbs.length > 0) {
      setIsTransitioning(false);
    }
  }, [displayBreadcrumbs, isTransitioning]);

  const buildNavigationPath = useCallback(
    (basePath: string): string => {
      const queryParams: string[] = [];
      const filters = searchParams.get('state');
      const accountNumber = searchParams.get('accountNumber');

      if (filters) queryParams.push(`state=${filters}`);
      if (accountNumber) queryParams.push(`accountNumber=${accountNumber}`);

      return queryParams.length > 0 ? `${basePath}?${queryParams.join('&')}` : basePath;
    },
    [searchParams]
  );

  const handleBreadcrumbNavigation = useCallback(
    (index: number) => {
      const targetPath = breadcrumbsToShow[index]?.fullPath;
      if (!targetPath) return;

      const navigationPath = buildNavigationPath(targetPath);
      navigate(navigationPath);
      navigateToBreadcrumb(index, navigate, searchParams);
    },
    [breadcrumbsToShow, navigate, navigateToBreadcrumb, searchParams, buildNavigationPath]
  );

  const renderBreadcrumbView = () => {
    const firstDrillDownCrumb = breadcrumbsToShow.find(
      bc => ATTRIBUTE_DETAIL_URLS.filter(adu => bc.path.includes(adu)).length > 0
    );
    const drilldownIndex = firstDrillDownCrumb ? breadcrumbsToShow.indexOf(firstDrillDownCrumb) : -1;

    let shouldTruncate;
    let hiddenStartIndex;
    let hiddenEndIndex;

    if (drilldownIndex > 0) {
      hiddenStartIndex = drilldownIndex + 1;
      hiddenEndIndex = breadcrumbsToShow.length - 2;
      shouldTruncate = breadcrumbsToShow.length - drilldownIndex > 2;
    } else {
      hiddenStartIndex = 1;
      hiddenEndIndex = breadcrumbsToShow.length - 3;
      shouldTruncate = breadcrumbsToShow.length >= 5;
    }

    if (!shouldTruncate) {
      return (
        <Breadcrumbs separator={<TbIcon name="ChevronRight" size="sm" />} data-testid="breadcrumb-full">
          {breadcrumbsToShow.map((item, idx) => {
            const isLast = idx === breadcrumbsToShow.length - 1;
            return isLast ? (
              <Typography key={item.fullPath} color="text.primary">
                {renderBreadcrumbLabel(item)}
              </Typography>
            ) : (
              <a
                className={textLinkClassName}
                key={item.fullPath}
                onClick={() => {
                  navigateToBreadcrumb(idx, navigate, searchParams);
                }}
              >
                {renderBreadcrumbLabel(item)}
              </a>
            );
          })}
        </Breadcrumbs>
      );
    }
    const lastItem = breadcrumbsToShow[breadcrumbsToShow.length - 1];

    const renderBCs = (indexFrom: number, indexTo: number) => {
      const rows: ReactNode[] = [];
      for (let i = indexFrom; i < indexTo; i++) {
        rows.push(
          <a className={textLinkClassName} onClick={() => handleBreadcrumbNavigation(i)}>
            {renderBreadcrumbLabel(breadcrumbsToShow[i])}
          </a>
        );
      }
      return rows;
    };

    return (
      <>
        <Breadcrumbs separator={<TbIcon name="ChevronRight" size="sm" />} data-testid="breadcrumb-truncated">
          {renderBCs(0, hiddenStartIndex)}

          <Button
            priority="ghost"
            size="sm"
            className={textLinkClassName}
            ref={ellipsisRef}
            onClick={handleEllipsisClick}
            data-testid="breadcrumb-ellipsis"
          >
            ...
          </Button>

          {renderBCs(hiddenEndIndex + 1, breadcrumbsToShow.length - 1)}

          <Typography color="text.primary">{renderBreadcrumbLabel(lastItem)}</Typography>
        </Breadcrumbs>

        <Menu
          anchorEl={anchorEl}
          open={dropdownOpen}
          onClose={handleDropdownClose}
          MenuListProps={{
            // @ts-expect-error - data-testid is not in MenuListProps but needed for testing
            'data-testid': 'breadcrumb-dropdown-menu',
          }}
        >
          {breadcrumbsToShow.slice(hiddenStartIndex, hiddenEndIndex + 1).map((hiddenItem, idx) => {
            const originalIndex = hiddenStartIndex + idx;
            return (
              <MenuItem
                key={hiddenItem.fullPath}
                onClick={() => {
                  handleBreadcrumbNavigation(originalIndex);
                  handleDropdownClose();
                }}
                data-testid={`breadcrumb-dropdown-item-${originalIndex}`}
              >
                <Box
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px',
                  }}
                >
                  {renderBreadcrumbLabel(hiddenItem)}
                </Box>
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  };

  return (
    <div className="flex flex-row items-center w-full" style={{ backgroundColor: tbPalette.background.default }}>
      {iconToShow && (
        <span className="flex items-center justify-center h-[34px] flex-shrink-0 text-ash-900 pr-3">{iconToShow}</span>
      )}
      <div
        className="py-1 min-w-0 flex-1 overflow-x-auto overflow-y-hidden"
        style={{ whiteSpace: 'nowrap', backgroundColor: tbPalette.background.default }}
      >
        {renderBreadcrumbView()}
      </div>
    </div>
  );
}
